import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/client';
import { uploadNumbers, uploadSettingsPayload, UPLOAD_SETTINGS } from '../utils/orderUnits';

// qty и цену (price | price_per_pcs) проверяет uploadNumbers; необязательные колонки:
// est_del_date, bo_follow_up_date, tracking, duties
const REQUIRED_COLUMNS = ['sku', 'year', 'month_num', 'supplierName', 'statusID'];
const VALID_STATUSES = ['S', 'OP', 'BO', 'D', 'C'];

/**
 * useUploadOrders
 * Универсальный хук для загрузки заказов из файла.
 * Поддерживает любой materialtypeID — тип выбирается прямо в попапе.
 * Цена в файле — в валюте, выбранной в окне загрузки (settings); в USD её пересчитывает сервер.
 *
 * @param {Array}    materialTypes  — список типов материалов [{ materialtypeID, materialName }]
 * @param {Array}    suppliers      — список поставщиков для выбранного типа [{ supplierID, supplierName }]
 * @param {Function} onSuccess      — callback после успешного сохранения
 */
export const useUploadOrders = ({ materialTypes = [], suppliers = [], onSuccess }) => {
  const [materialtypeID, setMaterialtypeID] = useState('');
  const [settings, setSettings]             = useState(UPLOAD_SETTINGS);
  const [parsedRows, setParsedRows]         = useState([]);
  const [parseErrors, setParseErrors]       = useState([]);
  const [orderID, setOrderID]               = useState('');
  const [fileName, setFileName]             = useState('');
  const [rawFile, setRawFile]               = useState(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [submitResult, setSubmitResult]     = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { message, duplicates }

  const reset = useCallback(() => {
    setSettings(UPLOAD_SETTINGS);
    setParsedRows([]);
    setParseErrors([]);
    setOrderID('');
    setFileName('');
    setIsSubmitting(false);
    setSubmitResult(null);
    setDuplicateWarning(null);
    // materialtypeID не сбрасываем — удобнее оставить выбор
  }, []);

  // Сброс результатов парсинга при смене типа (поставщики меняются)
  const handleMaterialTypeChange = useCallback((id) => {
    setMaterialtypeID(id);
    setParsedRows([]);
    setParseErrors([]);
    setFileName('');
    setSubmitResult(null);
  }, []);

  const validateRow = useCallback((row, index, supplierMap, validStatuses) => {
    const errors = [];

    for (const col of REQUIRED_COLUMNS) {
      if (row[col] === undefined || row[col] === null || String(row[col]).trim() === '') {
        errors.push(`Row ${index + 1}: Missing required field "${col}"`);
      }
    }

    const supplierKey = row.supplierName?.toString().trim();
    if (supplierKey && !supplierMap[supplierKey]) {
      errors.push(`Row ${index + 1}: Unknown supplier "${supplierKey}" for selected material type`);
    }

    uploadNumbers(row, false).errors.forEach(err => errors.push(`Row ${index + 1}: ${err}`));

    const status = row.statusID?.toString().trim().toUpperCase();
    if (status && !validStatuses.includes(status)) {
      errors.push(`Row ${index + 1}: Invalid statusID "${status}". Valid: ${validStatuses.join(', ')}`);
    }

    if (status !== 'BO' && !row.est_del_date) {
      errors.push(`Row ${index + 1}: "est_del_date" required when statusID is not BO`);
    }
    if (status === 'BO' && !row.bo_follow_up_date) {
      errors.push(`Row ${index + 1}: "bo_follow_up_date" required when statusID is BO`);
    }

    return errors;
  }, []);

  const parseFile = useCallback((file) => {
    if (!materialtypeID) {
      setParseErrors(['Please select a material type before uploading a file']);
      return;
    }

    setFileName(file.name);
    setRawFile(file);
    setParsedRows([]);
    setParseErrors([]);
    setSubmitResult(null);

    // Строим карту поставщиков для текущего типа
    const supplierMap = {};
    suppliers.forEach(s => { supplierMap[s.supplierName.trim()] = s.supplierID; });

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let rawRows = [];

        if (file.name.toLowerCase().endsWith('.csv')) {
          const text = e.target.result;
          const lines = text.split('\n').filter(l => l.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          rawRows = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj = {};
            headers.forEach((h, i) => { obj[h] = values[i] ?? ''; });
            return obj;
          }).filter(r => Object.values(r).some(v => v !== ''));
        } else {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        }

        const allErrors = [];
        const validRows = [];

        rawRows.forEach((row, index) => {
          const rowErrors = validateRow(row, index, supplierMap, VALID_STATUSES);
          if (rowErrors.length > 0) {
            allErrors.push(...rowErrors);
          } else {
            const status = row.statusID.toString().trim().toUpperCase();
            const { qty, price, duties } = uploadNumbers(row, false);
            validRows.push({
              sku:              row.sku?.toString().trim(),
              year:             parseInt(row.year),
              month_num:        parseInt(row.month_num),
              supplierName:     row.supplierName?.toString().trim(),
              supplierID:       supplierMap[row.supplierName?.toString().trim()],
              statusID:         status,
              qty,
              price,            // за штуку, в валюте окна загрузки
              duties,
              est_del_date:     status !== 'BO' ? (row.est_del_date || '') : null,
              bo_follow_up_date:status === 'BO' ? (row.bo_follow_up_date || '') : null,
              tracking:         row.tracking?.toString().trim() || '',
            });
          }
        });

        setParseErrors(allErrors);
        setParsedRows(validRows);
        // Файл с пошлинами — импортный заказ: галочка ставится сама, снять можно
        if (validRows.some(r => r.duties > 0)) setSettings(prev => ({ ...prev, is_import: true }));
      } catch (err) {
        setParseErrors([`Failed to parse file: ${err.message}`]);
      }
    };

    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  }, [materialtypeID, suppliers, validateRow]);

  const submitOrders = useCallback(async (force = false) => {
    if (!materialtypeID) {
      alert('Please select a material type');
      return;
    }
    if (!orderID.trim()) {
      alert('Order ID is required');
      return;
    }
    if (parsedRows.length === 0) {
      alert('No valid rows to submit');
      return;
    }
    const payload = uploadSettingsPayload(settings, false);
    if (payload.error) {
      setSubmitResult({ success: false, message: payload.error });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);
    setDuplicateWarning(null);

    try {
      // Цена уходит как введённая (price_entered), в USD её пересчитывает сервер
      const orders = parsedRows.map(({ price, ...row }) => ({
        ...row,
        price_entered: price,
        orderID: orderID.trim(),
        materialtypeID: parseInt(materialtypeID),
      }));

      const response = await api.post('/api/bulk_add_orders', { orders, force, settings: payload.settings });

      // Save original file to server for audit archive
      if (rawFile) {
        try {
          const fd = new FormData();
          fd.append('file', rawFile);
          await api.post('/api/material-orders/save-file/material', fd);
        } catch { /* non-critical — don't block success */ }
      }

      setSubmitResult({ success: true, message: `Successfully saved ${response.data.count} orders.` });
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error.response?.status === 409) {
        // Server found duplicate rows — ask user to confirm
        setDuplicateWarning({
          message: error.response.data.message,
          duplicates: error.response.data.duplicates || [],
        });
      } else {
        setSubmitResult({ success: false, message: error.response?.data?.error || 'Failed to save orders' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [materialtypeID, orderID, parsedRows, settings, rawFile, onSuccess]);

  return {
    materialtypeID,
    handleMaterialTypeChange,
    settings, setSettings,
    parsedRows,
    parseErrors,
    orderID, setOrderID,
    fileName,
    isSubmitting,
    submitResult,
    duplicateWarning,
    dismissDuplicateWarning: () => setDuplicateWarning(null),
    parseFile,
    submitOrders,
    reset,
  };
};